import store from '@/store';
import md5 from 'md5';
import router from '@/router';
import PaymentMethodLiteral from '@/utils/paymentMethodLiteral';
import { ConnectorFactory } from '@/services/connectors/connectorFactory';
import {
  BillingFrequencyOption,
  PaymentClearingType,
} from '@/types/PaymentTypes';
import { useCookies } from 'vue3-cookies';
import { PaymentRequest } from '@/models/PaymentServiceRequest';
import {
  getFirstNameLastNameFromAddresses,
  getGpNrOfPurchaser,
  getCountryCodeFromAddresses,
  getAddressesOfPurchaser,
} from '@/composables/general-functions';
import OrderService from '@/services/order-service'; // Ensure OrderService is imported
import { PayoneRequest } from '@/models/Payone';

export default class PaymentService {
  public static getServiceTicket() {
    return router.currentRoute.value.query.st;
  }

  public static getProcessId(): string {
    const { cookies } = useCookies();
    return cookies.get('processId');
  }

  public static getCtxToken(): string {
    const { cookies } = useCookies();
    return cookies.get('ctxToken');
  }

  public static storePayFrameUrl(absPayFrameUrl: string) {
    store.commit('changePayFrameUrl', absPayFrameUrl);
  }

  public static storeProcessId(processId: string) {
    const { cookies } = useCookies();
    cookies.set('processId', processId);
  }

  public static storeCtxToken(ctxToken: string) {
    const { cookies } = useCookies();
    cookies.set('ctxToken', ctxToken);
  }

  public static isPurchaserPaymentDataInvalid() {
    return (
      (store.state.paymentType === 'card' &&
        store.state.purchaserPaymentData.pseudoCardPan.length === 0) ||
      (store.state.paymentType === 'sepa' &&
        store.state.purchaserPaymentData.iban.length === 0) ||
      (store.state.formData.salesCampaign.withCosts &&
        !store.state.paymentMethodKeys.includes(
          store.state.purchaserPaymentData.paymentMethod,
        ))
    );
  }

  public static buildPayContextToken(customerId: string, waId: string) {
    return md5('ctxForSPSPaymentCallBack' + customerId + 'oscWAID' + waId);
  }

  public static readPayContextTokenParam() {
    return router.currentRoute.value.query.ctxtoken;
  }

  public static getBillingFrequencies(
    salesCampaignId: string,
    paymentMethod: PaymentMethodLiteral,
  ): Promise<BillingFrequencyOption[]> {
    const paymentConnector = ConnectorFactory.getPaymentConnector();
    return paymentConnector
      .getBillingFrequencies(salesCampaignId, paymentMethod)
      .then((response) => response.data)
      .catch((error) => console.log(error.response.data));
  }

  public static requestPayOneParameters(): Promise<PayoneRequest> {
    const paymentConnector = ConnectorFactory.getPaymentConnector();
    return paymentConnector
      .getPayOneParameters(store.state.formDataToken)
      .then((response) => response.data)
      .catch((error) => {
        console.log(error.response.data);
      });
  }

  public static async startPaymentAgreement(
    paymentType: PaymentClearingType,
    recurring?: boolean,
  ) {
    await OrderService.persistState();
    const formData = store.state.formData;
    const purchaserAddresses = getAddressesOfPurchaser();
    const { firstName, lastName } =
      getFirstNameLastNameFromAddresses(purchaserAddresses);
    const countryCode = getCountryCodeFromAddresses(purchaserAddresses);
    const customerId = getGpNrOfPurchaser();

    const orderName = formData.salesCampaign.name;

    // Converts to Cent, if priceWithDeliveryDate is called, priceWithDeliveryDate is to be used:
    let amountInCent: number = 100 * formData.salesCampaign.priceForTracking;

    if (store.state.priceResultFetchSuccessful) {
      if (store.state.priceResult.annualPrice > 0) {
        amountInCent = 100 * store.state.priceResult.annualPrice;
      } else {
        amountInCent = 100 * store.state.priceResult.price;
      }
    }
    // If this is a recurring payment agreement, no initial amount should be charged
    if (recurring) {
      amountInCent = 0;
    }
    // Build the request DTO
    const requestDto = this.buildRequestDto(paymentType, {
      customerId,
      firstName,
      lastName,
      countryCode,
      orderName,
      amountInCent,
    });

    const paymentConnector = ConnectorFactory.getPaymentConnector();

    return paymentConnector
      .getPaymentAuthorizationUrl(requestDto, store.state.formDataToken)
      .then((responseBody) => {
        if (responseBody.externalRedirectUrl == null) {
          store.commit('changeCreditCardDeclined', true);
          window.scrollTo(0, 0);
        } else {
          PaymentService.storePayFrameUrl(responseBody.externalRedirectUrl);
          PaymentService.storeCtxToken(responseBody.ctxToken);
          window.location.href = responseBody.externalRedirectUrl;
        }
      })
      .catch((error) => {
        console.log(error.response.data);
        router.push({
          name: 'ErrorPage',
          params: { type: 'server', supportId: error.response?.data?.supportId },
        });
      });
  }

  private static buildRequestDto(
    paymentType: PaymentClearingType,
    data: {
      customerId: string;
      firstName: string;
      lastName: string;
      countryCode?: string;
      orderName?: string;
      amountInCent?: number;
    },
  ): PaymentRequest {
    return {
      user: {
        customerId: data.customerId || '',
        firstname: data.firstName,
        lastname: data.lastName,
        countryCode: data.countryCode || 'DE',
      },
      cardData: {
        pseudocardpan: store.state.purchaserPaymentData?.pseudoCardPan || '',
        truncatedcardpan:
          store.state.purchaserPaymentData?.truncatedCardPan || '',
      },
      order: {
        orderName: data.orderName || 'Default Order Name',
        amountInCent: data.amountInCent || 0,
      },
      clearingType: paymentType,
    } as PaymentRequest;
  }
}
